import React from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import CoordinateMap from 'components/App/CoordinateMap';

import AppButton from 'components/App/Button';
import AppInputValidation from 'components/App/InputValidation';
import AppSelectValidation from 'components/App/SelectValidation';
import FormField from 'components/AddLocation/FormField';
import SectionWrapper from 'components/AddLocation/SectionWrapper';

import { ReactComponent as NextIconSVG } from 'assets/images/location/next-icon.svg';

import marketApi from 'api/marketApi';
import { LANGUAGE_LABEL, PARAM_SETTING } from 'constants/setting';
import TAB from 'constants/tab';
import { handleError } from 'utils';
import { editLocationFormSteps } from 'utils/validate';
import {
  starOptions,
  placeTypeOptions,
} from './config';

import styles from './StepBasic.module.scss';

function StepBasic(props) {
  const {
    username,
    provinces,
    districts,
    wards,
    setting,
    location,

    actionGetProvinceList,
    actionGetDistrictList,
    // actionGetWardList,
    actionAddLocationByStep,
    setIsValid,

    landmarkOptions,
    isLoadingLandmarkList,
    actionGetLandmarkList,
    actionClearLandmarkList,
  } = props;

  const [markets, setMarkets] = React.useState([]);
  const [isMapModalOpen, setIsMapModalOpen] = React.useState(false);
  const mapRef = React.useRef(null);

  const toggleMapModal = () => setIsMapModalOpen(!isMapModalOpen);

  const initialLandmarks = React.useMemo(() => {
    if (!location?.landmarks || !Array.isArray(location.landmarks)) {
      return [];
    }
    const allOptions = landmarkOptions.flatMap((group) => group.options || []);
    return location.landmarks.map((id) => {
      const option = allOptions.find((opt) => opt.value === id);
      return {
        value: id,
        label: option?.label || id,
      };
    });
  }, [location?.landmarks, landmarkOptions]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      placeType: location?.placeType || placeTypeOptions[0].value,
      name: location?.name || '',
      starRating: location?.starRating || '',
      placeContactName: location?.placeContact?.name || '',
      placeContactMainLanguage: location?.placeContact?.mainLanguage || '',
      placeContactRole: location?.placeContact?.role || '',
      placeContactEmail: location?.placeContact?.email || '',
      placeContactPhone: location?.placeContact?.phoneNumbers?.[0] || '',
      placeContactAnotherPhone: location?.placeContact?.phoneNumbers?.[1] || '',
      market: location?.market || '',
      province: provinces.find((p) => p.name === location?.province)?.id || '',
      district: districts.find((d) => d.name === location?.district)?.id || '',
      ward: wards.find((w) => w.name === location?.ward)?.id || '',
      address: location?.address || '',
      landmarks: initialLandmarks,
      coordinates: location?.coordinates || '',
      description: location?.description || '',
    },

    validationSchema: Yup.object(editLocationFormSteps.stepBasicValidation),

    onSubmit: (values) => {
      const phoneNumbers = [values.placeContactPhone, values.placeContactAnotherPhone]
        .filter(Boolean);
      const formattedPhoneNumbers = phoneNumbers.map((number) => {
        if (typeof number === 'string' && number.startsWith('0')) {
          return `+84${number.slice(1)}`;
        }
        return number;
      });

      const locationOfStep = {
        ...location,
        address: values.address,
        description: values.description,
        district: districts.find((district) => district.id === values.district)?.name || '',
        landmarks: values.landmarks.length > 0 ? values.landmarks.map((item) => item.value) : null,
        name: values.name,
        placeContact: {
          name: values.placeContactName,
          email: values.placeContactEmail,
          phoneNumbers: formattedPhoneNumbers,
          mainLanguage: values.placeContactMainLanguage,
          role: values.placeContactRole,
        },
        placeType: values.placeType,
        market: values.market,
        province: provinces.find((province) => province.id === values.province).name,
        starRating: values.starRating,
        ward: wards.find((ward) => ward.id === values.ward)?.name || '',
        coordinates: values.coordinates,
      };

      actionAddLocationByStep({
        step: TAB.ROOM_PRICE,
        location: locationOfStep,
      });
    },
  });

  const selectedProvinceName = React.useMemo(
    () => provinces.find((province) => province.id === validation.values.province)?.name,
    [provinces, validation.values.province],
  );

  const marketOptions = React.useMemo(() => {
    if (!selectedProvinceName) {
      return [];
    }

    return markets
      .filter((market) => (market.province || market.region) === selectedProvinceName)
      .map((market) => ({
        ...market,
        label: market.name,
        value: market._id || market.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [markets, selectedProvinceName]);

  const formattedProvinces = React.useMemo(() => provinces?.map((province) => ({
    ...province,
    label: province.name,
    value: province.id,
  })).sort((a, b) => a.label.localeCompare(b.label)),
  [provinces]);

  // const formattedDistricts = React.useMemo(() => districts?.map((district) => ({
  //   ...district,
  //   label: district.name,
  //   value: district.id,
  // })).sort((a, b) => a.label.localeCompare(b.label)), [districts]);

  // const formattedWards = React.useMemo(() => wards?.map((ward) => ({
  //   ...ward,
  //   label: ward.name,
  //   value: ward.id,
  // })).sort((a, b) => a.label.localeCompare(b.label)), [wards]);

  const formattedLanguageOptions = React.useMemo(() => {
    if (setting[PARAM_SETTING.LANGUAGES]) {
      const newLanguages = setting[PARAM_SETTING.LANGUAGES].map((item) => ({
        label: LANGUAGE_LABEL[item.name],
        value: item.name,
      }));

      return newLanguages;
    }

    return [];
  }, [setting]);

  const formattedRoleOptions = React.useMemo(() => {
    if (setting[PARAM_SETTING.ROLES]) {
      const newRoles = setting[PARAM_SETTING.ROLES].map((item) => ({
        label: item.vi,
        value: item.name,
      }));

      return newRoles;
    }

    return [];
  }, [setting]);

  const onChangeOption = React.useCallback((name) => (e) => {
    validation.setFieldValue(name, e.value, true);
    actionAddLocationByStep({
      step: TAB.BASIC,
      location: {
        [name]: e.value,
      },
    });
  }, []);

  const onChangeContactOption = React.useCallback((e) => {
    validation.setFieldValue(e.name, e.value, true);

    actionAddLocationByStep({
      step: TAB.BASIC,
      location: {
        ...location,
        placeContact: {
          ...location.placeContact,
          [e.id]: e.value,
        },
      },
    });
  }, [actionAddLocationByStep, location, validation]);

  const onChangeMarketOption = React.useCallback((e) => {
    validation.setFieldValue('market', e?.value || '', true);
    validation.setFieldValue('landmarks', [], true);
    actionAddLocationByStep({
      step: TAB.BASIC,
      location: {
        ...location,
        market: e?.value || '',
        landmarks: [],
      },
    });
  }, [actionAddLocationByStep, location, validation]);

  const onChangeAddressOption = React.useCallback((e) => {
    validation.setFieldValue(e.name, e.value, true);
    if (e.name === 'province') {
      validation.setFieldValue('landmarks', [], false);
      validation.setFieldValue('market', '', false);
    }
    actionAddLocationByStep({
      step: TAB.BASIC,
      location: {
        ...location,
        [e.name]: e.label,
        ...(e.name === 'province' ? { market: '' } : {}),
      },
    });
  }, [actionAddLocationByStep, location, validation]);

  const onChangeInputField = React.useCallback((e) => {
    const { value, name } = e.target;

    validation.setFieldValue(name, value, false);

    actionAddLocationByStep({
      step: TAB.BASIC,
      location: {
        [name]: value,
      },
    });
  }, [actionAddLocationByStep, validation]);

  const onChangePlaceContactField = React.useCallback(
    (e) => {
      const { name, value, id } = e.target;
      validation.setFieldValue(name, value, false);
      actionAddLocationByStep({
        step: TAB.BASIC,
        location: {
          ...location,
          placeContact: {
            ...location.placeContact,
            [id]: value,
          },
        },
      });
    },
    [actionAddLocationByStep, location, validation],
  );

  const onChangePhoneNumbers = React.useCallback((e) => {
    const { name, value } = e.target;
    validation.setFieldValue(name, value);

    const newLocation = { ...location };

    if (!newLocation.placeContact) {
      newLocation.placeContact = {};
    }

    if (!newLocation.placeContact.phoneNumbers) {
      newLocation.placeContact.phoneNumbers = [];
    }

    if (name === 'placeContactPhone') {
      if (newLocation.placeContact.phoneNumbers.length === 0) {
        newLocation.placeContact.phoneNumbers.push(value.startsWith('0') ? `+84${value.slice(1)}` : value);
      } else {
        newLocation.placeContact.phoneNumbers[0] = value.startsWith('0') ? `+84${value.slice(1)}` : value;
      }
    } else if (name === 'placeContactAnotherPhone') {
      if (newLocation.placeContact.phoneNumbers.length === 1) {
        newLocation.placeContact.phoneNumbers.push(value.startsWith('0') ? `+84${value.slice(1)}` : value);
      } else {
        newLocation.placeContact.phoneNumbers[1] = value.startsWith('0') ? `+84${value.slice(1)}` : value;
      }
    }

    actionAddLocationByStep({
      step: TAB.BASIC,
      location: newLocation,
    });

    validation.setFieldValue(name, value);
  }, [actionAddLocationByStep, location, validation]);

  const onChangeMultiOption = React.useCallback((name) => (e) => {
    validation.setFieldValue(name, e, true);
    actionAddLocationByStep({
      step: TAB.BASIC,
      location: {
        ...location,
        [name]: e.length > 0 ? e.map((item) => item.value) : null,
      },
    });
  }, [actionAddLocationByStep, location, validation]);

  const onChangeCoordinates = React.useCallback((e) => {
    const stringCoordinates = e.target.value;
    validation.setFieldValue('coordinates', stringCoordinates);
    actionAddLocationByStep({
      step: TAB.BASIC,
      location: {
        ...location,
        coordinates: stringCoordinates,
      },
    });

    const lat = parseFloat(stringCoordinates?.split(',')[0]);
    const lng = parseFloat(stringCoordinates?.split(',')[1]);
    if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
      mapRef.current?.panTo({ lat, lng });
    }
  }, [actionAddLocationByStep, location, validation]);

  React.useEffect(() => {
    if (validation.values.coordinates !== location?.coordinates) {
      actionAddLocationByStep({
        step: TAB.BASIC,
        location: {
          ...location,
          coordinates: validation.values.coordinates,
        },
      });
    }
  }, [validation.values.coordinates, location?.coordinates, actionAddLocationByStep, location]);

  React.useEffect(() => {
    const fetchMarkets = async () => {
      try {
        const response = await marketApi.getMarkets({
          page: 1,
          limit: 500,
          isPublished: true,
        });

        if (response?.payload?.data) {
          setMarkets(response.payload.data);
        }
      } catch (error) {
        handleError(error);
      }
    };

    fetchMarkets();
  }, []);

  React.useEffect(() => {
    actionGetProvinceList({});
  }, []);

  React.useEffect(() => {
    if (validation.values.province) {
      actionGetDistrictList({ provinces: validation.values.province });
    }
  }, [validation.values.province]);

  React.useEffect(() => {
    if (validation.values.market) {
      const selectedMarket = marketOptions.find((m) => m.value === validation.values.market);
      actionGetLandmarkList({
        market: validation.values.market,
        marketName: selectedMarket?.label || '',
      });
    } else {
      actionClearLandmarkList();
    }

    return () => {
      actionClearLandmarkList();
    };
  }, [validation.values.market, marketOptions, actionGetLandmarkList, actionClearLandmarkList]);

  // React.useEffect(() => {
  //   validation.setFieldValue('ward', '', false);

  //   if (validation.values.district && validation.values.province) {
  //     actionGetWardList({
  //       provinces: validation.values.province,
  //       districts: validation.values.district,
  //     });
  //   }
  // }, [validation.values.district]);

  React.useEffect(() => {
    setIsValid(validation.isValid);
  }, [setIsValid, validation.isValid]);

  return (
    <div className={styles.Component}>
      <form onSubmit={validation.handleSubmit} className="d-flex flex-column gap-4">
        <SectionWrapper title={`Xin chào ${username}!`}>
          <FormField label="Loại hình" isRequired>
            <AppSelectValidation
              placeholder="Khách sạn"
              name="placeType"
              key="placeType"
              options={placeTypeOptions}
              validation={validation}
              onChange={onChangeOption('placeType')}
              value={validation.values.placeType}
            />
          </FormField>

          <FormField label="Tên chỗ nghỉ" isRequired>
            <AppInputValidation
              isCounterOnInput
              isCustomOnChange
              name="name"
              id="name"
              type="text"
              subTitle="Tên này sẽ được hiển thị tới khách khi họ tìm kiếm chỗ nghỉ."
              placeholder="Nhập tên tuỳ chọn"
              validation={validation}
              limitCharacter={100}
              onChange={onChangeInputField}
            />
          </FormField>

          <FormField label="Hạng sao" isRequired>
            <AppSelectValidation
              placeholder="Vui lòng chọn hạng sao"
              name="starRating"
              options={starOptions}
              validation={validation}
              onChange={onChangeOption('starRating')}
              value={validation.values.starRating}
            />
          </FormField>
        </SectionWrapper>

        <SectionWrapper title="Chi tiết liên hệ của chỗ nghỉ là gì?">
          <FormField label="Tên người liên hệ" isRequired>
            <AppInputValidation
              isCounterOnInput
              isCustomOnChange
              type="text"
              id="name"
              name="placeContactName"
              placeholder="Nhập Tên người liên hệ chính của chỗ nghỉ"
              validation={validation}
              limitCharacter={100}
              onChange={onChangePlaceContactField}
            />
          </FormField>

          <FormField label="Ngôn ngữ ưu tiên" isRequired>
            <AppSelectValidation
              placeholder="Vui lòng chọn ngôn ngữ ưu tiên"
              name="placeContactMainLanguage"
              id="mainLanguage"
              options={formattedLanguageOptions}
              validation={validation}
              onChange={onChangeContactOption}
              value={validation.values.placeContactMainLanguage}
            />
          </FormField>

          <FormField label="Chức vụ" isRequired>
            <AppSelectValidation
              placeholder="Vui lòng chọn chức vụ"
              id="role"
              name="placeContactRole"
              options={formattedRoleOptions}
              validation={validation}
              onChange={onChangeContactOption}
              value={validation.values.placeContactRole}
            />
          </FormField>

          <FormField label="Địa chỉ Email" isRequired>
            <AppInputValidation
              isCustomOnChange
              type="text"
              id="email"
              name="placeContactEmail"
              placeholder="Nhập địa chỉ Email người liên hệ"
              validation={validation}
              onChange={onChangePlaceContactField}
            />
          </FormField>

          <FormField label="Số điện thoại liên lạc" isRequired>
            <AppInputValidation
              type="text"
              id="phoneNumbers"
              name="placeContactPhone"
              placeholder="Nhập số điện thoại người liên hệ"
              validation={validation}
              isCustomOnChange
              onChange={onChangePhoneNumbers}
            />
          </FormField>

          <FormField label="Số điện thoại khác">
            <AppInputValidation
              type="text"
              id="phoneNumbers"
              name="placeContactAnotherPhone"
              placeholder="Không bắt buộc"
              validation={validation}
              isCustomOnChange
              onChange={onChangePhoneNumbers}
            />
          </FormField>
        </SectionWrapper>

        <SectionWrapper title="Chỗ nghỉ tọa lạc ở đâu?">

          <FormField label="Tỉnh/ Thành phố" isRequired>
            <AppSelectValidation
              options={formattedProvinces}
              isHiddenSearchable
              placeholder="Vui lòng chọn thành phố"
              value={validation.values.province}
              name="province"
              validation={validation}
              onChange={onChangeAddressOption}
            />
          </FormField>

          <FormField label="Thị trường" isRequired>
            <AppSelectValidation
              options={marketOptions}
              isHiddenSearchable
              placeholder="Vui lòng chọn thị trường"
              value={validation.values.market}
              name="market"
              validation={validation}
              onChange={onChangeMarketOption}
              isDisabled={!validation.values.province}
            />
          </FormField>

          {/* <FormField label="Quận/ Huyện" isRequired>
            <AppSelectValidation
              placeholder="Vui lòng chọn quận huyện"
              options={formattedDistricts}
              isHiddenSearchable
              value={validation.values.district}
              name="district"
              validation={validation}
              onChange={onChangeAddressOption}
              isDisabled={!validation.values.province}
            />
          </FormField>

          <FormField label="Phường/ Xã">
            <AppSelectValidation
              placeholder="Vui lòng chọn phường xã"
              isHiddenSearchable
              options={formattedWards}
              value={validation.values.ward}
              name="ward"
              validation={validation}
              onChange={onChangeAddressOption}
              isDisabled={!validation.values.district}
            />
          </FormField> */}

          <FormField label="Địa chỉ" isRequired>
            <AppInputValidation
              isCounterOnInput
              isCustomOnChange
              type="text"
              id="address"
              name="address"
              validation={validation}
              limitCharacter={200}
              onChange={onChangeInputField}
            />
          </FormField>

          <FormField label="Khu vực">
            <AppSelectValidation
              placeholder="Vui lòng chọn khu vực"
              options={landmarkOptions}
              value={validation.values.landmarks}
              name="landmarks"
              validation={validation}
              onChangeMulti={onChangeMultiOption('landmarks')}
              isMulti
              isHiddenClearable
              isDisabled={!validation.values.market}
              isLoading={isLoadingLandmarkList}
              isGroup
            />
          </FormField>

          <FormField label="Định vị" isRequired>
            <AppInputValidation
              isCounterOnInput
              isCustomOnChange
              type="text"
              id="coordinates"
              name="coordinates"
              validation={validation}
              limitCharacter={100}
              onChange={onChangeCoordinates}
            />

            <div className="text-start">
              Trên
              {' '}
              <button
                type="button"
                onClick={toggleMapModal}
                className={styles.Hyperlink}
                style={{
                  border: 'none',
                  background: 'none',
                  padding: 0,
                  outline: 'none',
                }}
              >
                Google Maps
              </button>
            </div>
          </FormField>

          <FormField label="Miêu tả chỗ nghỉ" isRequired>
            <AppInputValidation
              isCounterOnInput
              isCustomOnChange
              type="text"
              id="description"
              name="description"
              validation={validation}
              limitCharacter={2000}
              onChange={onChangeInputField}
            />
          </FormField>
        </SectionWrapper>

        <div className={styles.FormAction}>
          <AppButton
            classes="Btn Btn__NextStep"
            type="submit"
          >
            Bước tiếp theo
            <NextIconSVG className="Icon__NextStep" />
          </AppButton>
        </div>
      </form>

      <Modal isOpen={isMapModalOpen} toggle={toggleMapModal} size="lg" centered>
        <ModalHeader toggle={toggleMapModal}>Chọn tọa độ trên bản đồ</ModalHeader>
        <ModalBody>
          <div className={styles.MapModalContent}>
            <CoordinateMap
              defaultPosition={validation.values.coordinates}
              validation={validation}
              mapRef={mapRef}
            />
          </div>
          <div className="d-flex justify-content-end mt-3">
            <AppButton type="button" onClick={toggleMapModal}>
              Xác nhận
            </AppButton>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
}

StepBasic.propTypes = {
  username: PropTypes.string.isRequired,

  setting: PropTypes.instanceOf(Object).isRequired,
  provinces: PropTypes.instanceOf(Array).isRequired,
  districts: PropTypes.instanceOf(Array).isRequired,
  wards: PropTypes.instanceOf(Array).isRequired,
  location: PropTypes.instanceOf(Object).isRequired,

  actionGetProvinceList: PropTypes.func.isRequired,
  actionGetDistrictList: PropTypes.func.isRequired,
  // actionGetWardList: PropTypes.func.isRequired,
  actionAddLocationByStep: PropTypes.func.isRequired,
  setIsValid: PropTypes.func.isRequired,

  landmarkOptions: PropTypes.instanceOf(Array),
  isLoadingLandmarkList: PropTypes.bool,
  actionGetLandmarkList: PropTypes.func.isRequired,
  actionClearLandmarkList: PropTypes.func.isRequired,
};

StepBasic.defaultProps = {
  landmarkOptions: [],
  isLoadingLandmarkList: false,
};

export default React.memo(StepBasic);
